<script setup>
import { gsap } from 'gsap';
import { onMounted, useCssModule } from 'vue';

const $style = useCssModule();

onMounted(() => {
  const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.5, delay: 1 });

  tl.add('startPaymentFlow')
    .to(`.${$style['bar__dynamic']}`, {
      opacity: 1,
      duration: 1,
      ease: 'power1.out',
    })
    .to(
      `.${$style['bar-label__text--payment']}`,
      {
        opacity: 1,
        duration: 1,
        ease: 'power1.out',
      },
      '<',
    )

    .add('paymentDisappearsAndUnlockFlow')
    .to(`.${$style['bar-label__text--payment']}`, {
      delay: 0.5,
      opacity: 0,
      duration: 0.3,
      ease: 'power1.out',
    })
    .to(
      `.${$style['bar__static']}`,
      {
        x: '0%',
        duration: 0.75,
        ease: 'power1.out',
      },
      '<0.1',
    )
    .to(
      `.${$style['bar-label__text--unlocked']}`,
      {
        opacity: 1,
        x: '0%',
        duration: 0.75,
        ease: 'power1.out',
      },
      '<',
    )
    .to(
      `.${$style['heading-amount--used-before']}`,
      {
        opacity: 0,
        y: '18px',
        duration: 0.5,
        ease: 'power1.out',
      },
      '<',
    )
    .to(
      `.${$style['heading-amount--used-after']}`,
      {
        opacity: 1,
        y: '0',
        duration: 0.5,
        ease: 'power1.out',
      },
      '<',
    )

    .add('resetToInitialState')
    .to(`.${$style['bar-label__text--unlocked']}`, {
      delay: 1,
      opacity: 0,
      duration: 0.3,
      ease: 'power1.out',
    })
    .set(`.${$style['heading-amount--used-before']}`, { y: '0' }, '<')
    .to(
      `.${$style['heading-amount--used-before']}`,
      {
        opacity: 1,
        duration: 0.3,
        ease: 'power1.out',
      },
      '<',
    )
    .set(`.${$style['heading-amount--used-after']}`, { y: '0' }, '<')
    .to(
      `.${$style['heading-amount--used-after']}`,
      {
        opacity: 0,
        duration: 0.3,
        ease: 'power1.out',
      },
      '<',
    )
    .to(
      `.${$style['bar__static']}`,
      {
        x: '-13%',
        duration: 0.5,
        ease: 'power1.out',
      },
      '<',
    )
    .set(`.${$style['bar__dynamic']}`, { opacity: 0 }, '<');
});
</script>

<template>
  <div :class="$style.animation">
    <div :class="$style.heading">
      <div :class="$style.heading__left">
        <div :class="$style['heading-title']">Available Capacity</div>
        <div
          :class="[
            $style['heading-amount'],
            $style['heading-amount--used-before'],
          ]"
        >
          $30,000
        </div>
        <div
          :class="[
            $style['heading-amount'],
            $style['heading-amount--used-after'],
          ]"
        >
          $32,500
        </div>
      </div>
      <div :class="$style.heading__right">
        <div :class="$style['heading-title']">Used Capacity</div>
        <div
          :class="[
            $style['heading-amount'],
            $style['heading-amount--used-before'],
          ]"
        >
          $20,000
        </div>
        <div
          :class="[
            $style['heading-amount'],
            $style['heading-amount--used-after'],
          ]"
        >
          $17,500
        </div>
      </div>
    </div>
    <div :class="$style.bar">
      <div :class="$style['bar__static']"></div>
      <div :class="$style['bar__dynamic']"></div>
    </div>
    <div :class="$style['bar-label']">
      <div
        :class="[$style['bar-label__text'], $style['bar-label__text--payment']]"
      >
        <span>Weekly payment</span>
      </div>
      <div
        :class="[
          $style['bar-label__text'],
          $style['bar-label__text--unlocked'],
        ]"
      >
        <span>+$2,500 unlocked</span>
      </div>
    </div>
  </div>
</template>

<style lang="less" module>
.animation {
  background: #f8f8fa;
  border-radius: 16px;
  padding: 24px;
  overflow: hidden;
}

.heading {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.heading__left {
  position: relative;
}

.heading__right {
  text-align: right;
  position: relative;
}

.heading-title {
  margin-bottom: 4px;
  font-size: 11px;
  font-family: @regular-font;
  font-weight: 700;
  color: #5f5f61;
}

.heading-amount {
  font-size: 15px;
  font-family: @regular-font;
  font-weight: 700;
  color: #111111;
}

.heading-amount--used-before {
  opacity: 1;
}

.heading-amount--used-after {
  position: absolute;
  bottom: 0;
  opacity: 0;
  transform: translateY(-18px);
}

.heading__right .heading-amount--used-after {
  right: 0;
}

.bar {
  height: 8px;
  background: #ceced0;
  border-radius: 8px;
  margin-bottom: 6px;
  display: flex;
  position: relative;
  overflow: hidden;
}

.bar__static {
  background: #7f30ff;
  width: 74%;
  position: relative;
  z-index: 10;
  transform: translateX(-13%);
}

.bar__dynamic {
  position: absolute;
  top: 0;
  left: 64%;
  height: 8px;
  width: 10%;
  background: repeating-linear-gradient(to right, #7f30ff 1px, #fff 3px);
  opacity: 0;
}

.bar-label {
  position: relative;
}

.bar-label__text {
  transform: translateX(64%);
  font-size: 12px;
  font-family: @regular-font;
  font-weight: 600;
  white-space: nowrap;
}

.bar-label__text span {
  display: inline-block;
  transform: translateX(-50%);
}

.bar-label__text--payment {
  opacity: 0;
  transform: translateX(68%);
}

.bar-label__text--unlocked {
  position: absolute;
  top: 0;
  left: 74%;
  transform: translateX(-30%);
  opacity: 0;
}
</style>
